import AttachMoney from "@mui/icons-material/AttachMoney";
import MoneyOff from "@mui/icons-material/MoneyOff";
import { ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRevealWageActions } from "@/hooks/useRevealWage";
import { CHROME_OVERLAY_ALPHA, MOTION, SURFACE } from "@/theme/tokens";
import SystemDialog from "@/ui/Popups/Dialog";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { menuRowStyles } from "./utils";

type Props = {
	/**
	 * How the control is drawn. The BEHAVIOUR is identical in both — that is the point of the prop.
	 *
	 * `header` is the icon button on the app bar's gradient; `menuRow` is a list row for a menu or sheet
	 * that stands in for the header, styled like the rows around it.
	 */
	variant?: "header" | "menuRow";
	/** Overrides the per-variant default so a second placement can be targeted in tests. */
	dataTestId?: string;
	/** Fires after a toggle actually takes effect, for a caller that tracks it in its own surface. */
	onToggle?: (revealWage: boolean) => void;
};

/**
 * The show/hide-wages control, in whichever chrome is currently on screen.
 *
 * ONE component for both placements, because the rules around wages are the part that must not be
 * copied: who may see the control at all (`viewWageAllowed`), and the asymmetry that revealing asks
 * first while hiding is immediate. The gaps-first More sheet briefly reimplemented all of it beside
 * this file, and the two confirmation dialogs had already drifted apart — different wording, and only
 * one of them translated. A `variant` keeps the markup difference, which is real, and nothing else.
 */
export const WageRevealButton: FC<Props> = ({ variant = "header", dataTestId, onToggle }) => {
	const { viewWageAllowed, revealWage, setRevealWage } = useRevealWageActions();
	const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
	const { t } = useTranslation();
	const wageLabel = revealWage ? t("hide.wage") : t("show.wage");

	const applyReveal = (reveal: boolean): void => {
		setRevealWage(reveal);
		onToggle?.(reveal);
	};

	const handleConfirmClick = (): void => {
		if (!viewWageAllowed) return;
		applyReveal(true);
		setShowConfirmDialog(false);
	};

	/**
	 * Revealing asks first; hiding does not.
	 *
	 * Wages are the one thing here a person standing behind the user must not see by accident, and the
	 * reverse — hiding something already on screen — can never be the mistake worth a dialog.
	 */
	const handleToggleClick = (): void => {
		if (!viewWageAllowed) return;
		if (!revealWage) {
			setShowConfirmDialog(true);
			return;
		}
		applyReveal(false);
	};

	// Hidden entirely rather than disabled: an unusable money toggle is an invitation to ask for access.
	if (!viewWageAllowed) return null;

	const icon = revealWage ? <AttachMoney /> : <MoneyOff />;

	const control: ReactElement =
		variant === "menuRow" ? (
			<ListItemButton data-testid={dataTestId ?? "menu-wage-reveal-toggle"} onClick={handleToggleClick} sx={{ borderRadius: 1 }}>
				<ListItemIcon sx={menuRowStyles.iconContainer}>
					{revealWage ? <MoneyOff sx={menuRowStyles.icon} /> : <AttachMoney sx={menuRowStyles.icon} />}
				</ListItemIcon>
				<ListItemText primary={wageLabel} sx={menuRowStyles.text} />
			</ListItemButton>
		) : (
			<SystemButton
				tooltip={wageLabel}
				// Icon-only, so the tooltip is the only thing naming it — and a tooltip is not an accessible
				// name. Screen readers announced an unlabelled button.
				aria-label={wageLabel}
				data-testid={dataTestId ?? "header-wage-reveal-toggle"}
				onClick={handleToggleClick}
				iconButton
				leadingIcon={icon}
				sx={{
					color: SURFACE,
					// The header gradient is this button's background, so "engaged" has to be drawn in white:
					// a low-alpha fill plus a solid hairline outline. A swapped glyph alone was too easy to miss.
					// The border is present in both states (transparent when wages are revealed, which keeps the
					// ghost look) so toggling only ever changes color — the control's box never moves.
					border: "1px solid",
					borderColor: revealWage ? "transparent" : SURFACE,
					bgcolor: revealWage ? "transparent" : alpha(SURFACE, CHROME_OVERLAY_ALPHA.TOGGLE_ACTIVE),
					transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, border-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
					"&:hover": {
						bgcolor: alpha(SURFACE, revealWage ? CHROME_OVERLAY_ALPHA.HOVER : CHROME_OVERLAY_ALPHA.TOGGLE_ACTIVE_HOVER),
						borderColor: revealWage ? "transparent" : SURFACE,
					},
				}}
			/>
		);

	return (
		<>
			{control}
			<SystemDialog
				open={showConfirmDialog}
				onClose={() => setShowConfirmDialog(false)}
				title={t("wage.reveal.title")}
				body={t("wage.reveal.body")}
				cancel={t("cancel")}
				confirm={t("show.wage")}
				onConfirm={handleConfirmClick}
				dataTestId="wage-reveal-dialog"
			/>
		</>
	);
};
